/**
 * Care Report Service
 * Manages care_report_templates — per-client custom question configurations.
 *
 * Each template holds a client_name (unique) and a custom_questions JSONB array.
 * Question shape: { id, text, type, required, order }
 *   type: "text" | "textarea" | "checkbox" | "number"
 *
 * Run this SQL in Supabase before using this service:
 *
 *   CREATE TABLE care_report_templates (
 *       id               UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
 *       client_name      TEXT NOT NULL UNIQUE,
 *       custom_questions JSONB NOT NULL DEFAULT '[]'::jsonb,
 *       created_at       TIMESTAMPTZ NOT NULL DEFAULT NOW(),
 *       updated_at       TIMESTAMPTZ NOT NULL DEFAULT NOW()
 *   );
 *   CREATE TRIGGER trg_care_report_templates_updated_at
 *   BEFORE UPDATE ON care_report_templates
 *   FOR EACH ROW EXECUTE FUNCTION set_updated_at();
 */

import { supabase } from '../../utils/supabase_config';
import { SupabaseService } from './supabaseService';
import { TABLES } from './tables';

const TABLE = TABLES.CARE_REPORT_TEMPLATES;

export class CareReportService extends SupabaseService {
  /**
   * Get the template for a specific client.
   * @param {string} clientName
   * @returns {Promise<Object|null>} Template row or null if none exists
   */
  static async getTemplateForClient(clientName) {
    try {
      const { data, error } = await supabase
        .from(TABLE)
        .select('*')
        .eq('client_name', clientName)
        .maybeSingle();
      if (error) throw error;
      return data ? this._addDocId(data) : null;
    } catch (error) {
      this.handleError('Error fetching care report template', error);
      throw error;
    }
  }

  /**
   * Create or update the template for a client.
   * @param {string} clientName
   * @param {Array} customQuestions - Array of question objects
   */
  static async upsertTemplate(clientName, customQuestions) {
    try {
      const { error } = await supabase
        .from(TABLE)
        .upsert(
          { client_name: clientName, custom_questions: customQuestions },
          { onConflict: 'client_name' }
        );
      if (error) throw error;
    } catch (error) {
      this.handleError('Error saving care report template', error);
      throw error;
    }
  }

  /**
   * Get all templates (for admin overview).
   */
  static async getAllTemplates() {
    return super.getAll(TABLE, { orderBy: { field: 'client_name', direction: 'asc' } });
  }
}
